import { CheckOutlined } from '@ant-design/icons';
import { Icon } from '@iconify/react';
import { Select as AntdSelect, Checkbox as AntdCheckbox } from 'antd';
import type {
  SelectProps as AntdSelectProps,
  DefaultOptionType,
} from 'antd/es/select';
import { useCallback, forwardRef, useMemo, ComponentRef } from 'react';
import { colors } from 'theme/colors';

import { Tooltip } from 'components/Tooltip';

import { trackEvent } from 'utils/analytics';

import { useAuthStore } from 'store/auth.store';
import { useWorkspaceStore } from 'store/workspace.store';

const extractTextFromElement = (element: any): string => {
  if (typeof element === 'string' || typeof element === 'number') {
    return String(element);
  }

  if (element?.props?.children) {
    const children = Array.isArray(element.props.children)
      ? element.props.children
      : [element.props.children];
    return children.map(extractTextFromElement).join(' ');
  }

  return '';
};

const filterOptionWithOptionRender = (
  input: string,
  option: DefaultOptionType | undefined
) => {
  const inNorm = input.toLocaleLowerCase();
  const searchText = option?.searchText;
  if (searchText && typeof searchText === 'string')
    return searchText.includes(inNorm);
  const raw = option?.label;
  const text = extractTextFromElement(raw) || String(option?.value || '');
  return text.toLocaleLowerCase().includes(inNorm);
};

export interface SelectComponentProps extends AntdSelectProps {
  fullWidth?: boolean;
  dataCy?: string;
  message?: {
    eventName: string;
    properties?: Record<string, any>;
  };
}

export const Select = forwardRef<
  ComponentRef<typeof AntdSelect>,
  SelectComponentProps
>((props, ref) => {
  const { workspace } = useWorkspaceStore();
  const { user } = useAuthStore();

  const {
    value,
    fullWidth = true,
    dataCy,
    message,
    onChange,
    ...restProps
  } = props;

  const isItemSelected = useCallback(
    (option: DefaultOptionType) => {
      return Array.isArray(value)
        ? value.some((v) => v === option.value)
        : value === option.value;
    },
    [value]
  );

  const styles = useMemo(
    () =>
      ({
        popup: {
          root: {
            border: `1px solid ${colors.colorBorder}`,
            boxShadow: 'none',
            minWidth: '150px',
            ...(props.styles as any)?.popup?.root,
          },
        },
        root: {
          width: fullWidth ? '100%' : 'fit-content',
          minWidth: '150px',
          ...(props.styles as any)?.root,
        },
      }) as AntdSelectProps['styles'],
    [props.styles, fullWidth]
  );

  const multipleOptionRender = useCallback(
    (option: DefaultOptionType) => {
      return (
        <AntdCheckbox
          checked={isItemSelected(option)}
          className="w-full [&_.ant-checkbox-label]:w-[calc(100%-1rem)] [&_.ant-checkbox-label]:pr-0"
        >
          {option.label}
        </AntdCheckbox>
      );
    },
    [isItemSelected]
  );

  const menuItemSelectedIcon = useMemo(() => {
    return (
      props.mode !== 'multiple' && <CheckOutlined className="text-primary" />
    );
  }, [props.mode]);

  const maxTagPlaceholder = useCallback(
    (omittedValues: any[]) => (
      <Tooltip
        styles={{ root: { pointerEvents: 'none' } }}
        title={omittedValues.map(({ label }) => label).join(', ')}
      >
        <span>+{omittedValues.length} more...</span>
      </Tooltip>
    ),
    []
  );

  const optionRender = useMemo(() => {
    return (
      props.optionRender ??
      ((props.mode === 'multiple' && multipleOptionRender) || undefined)
    );
  }, [props.mode, multipleOptionRender, props.optionRender]);

  const suffixIcon = useMemo(() => {
    return (
      props.suffixIcon ?? (
        <Icon icon="mdi:chevron-down" style={{ fontSize: 16 }} />
      )
    );
  }, [props.suffixIcon]);

  const showSearchConfig = useMemo(() => {
    if (props.showSearch === false) return false; // If explicitly disabled, don't show search
    const baseConfig = {
      optionFilterProp: 'label' as const,
      filterOption: filterOptionWithOptionRender,
    };
    if (typeof props.showSearch === 'object' && props.showSearch !== null) {
      return {
        ...baseConfig, // If showSearch is an object, merge with defaults
        ...props.showSearch,
      };
    }
    return baseConfig; // showSearch is true or undefined - use defaults
  }, [props.showSearch]);

  return (
    <AntdSelect
      ref={ref}
      {...restProps}
      onChange={(value, option) => {
        trackEvent(message?.eventName ?? 'Select Change', {
          ...(message?.properties ?? {}),
          value,
          user_email: user?.email ?? '',
          user_name: user?.displayName ?? '',
          organization_id: workspace?.id ?? '',
          organization_name: workspace?.name ?? '',
          organization_email_domain: workspace?.email_domain ?? '',
          action_type: 'select',
        });

        onChange?.(value, option);
      }}
      data-cy={dataCy}
      value={value}
      menuItemSelectedIcon={
        restProps.menuItemSelectedIcon ?? menuItemSelectedIcon
      }
      optionRender={optionRender}
      styles={styles}
      maxTagCount={props.maxTagCount ?? 'responsive'}
      notFoundContent={
        <span style={{ color: colors.colorBorder }}>No matches found</span>
      }
      maxTagPlaceholder={props.maxTagPlaceholder ?? maxTagPlaceholder}
      showSearch={showSearchConfig}
      suffixIcon={suffixIcon}
    />
  );
});

Select.displayName = 'Select';
